import React, { useMemo, useState } from 'react';

import { InstructionStepIcon } from '@components/utilities/Bookings/InstructionStepIcon';
import { getInstructionStatusLabel } from '@data/instruction';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { InstructionStatus, InstructionType } from '@models/Instruction';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createStrings } from '@utils/local';
import { toJST } from '@utils/types';
import clsx from 'clsx';
import { orderBy } from 'lodash-es';

const strings = createStrings({
  ja: {
    seeAll: 'ステータス履歴を全部見る',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  instructionStatusStepsWrapper: {
    ...common.flexColumn,
    rowGap: '0.5rem',
  },
  instructionStatusStepsContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    rowGap: '0.25rem',
    columnGap: '5px',
  },
  instructionStatusStepsSeeAllButton: {
    ...common.textGray700,
    ...common.exSmallerText,
    ...common.baseButton,
    ...common.flexInline,
    ...common.justifyCenter,
    ...common.itemsCenter,
    columnGap: '0.5rem',
    backgroundColor: theme.palette.grey[50],
    height: 24,
  },
  open: {},
}));

const InstructionStatusStep: React.FC<{
  status: InstructionStatus;
  isOverdue: boolean;
  type?: InstructionType;
}> = ({ status, isOverdue }) => {
  const isAdmin = useIsAdmin();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const hasMessage = !!status.message?.trim();

  return (
    <>
      <div style={{ gridRow: `span ${hasMessage ? 3 : 2}`, paddingTop: 3 }}>
        <InstructionStepIcon status={status} isOverdue={isOverdue} />
      </div>

      <div className={clsx(classes.middleText, classes.textGray800)}>
        {getInstructionStatusLabel(status.name, isAdmin)}
      </div>

      {hasMessage && (
        <div
          className={clsx(classes.exSmallerText, classes.textGray700, classes.whitespacePreWrap)}
        >
          {status.message?.trim()}
        </div>
      )}

      <div className={clsx(classes.exSmallerText, classes.textGray500)}>
        {toJST(new Date(status.time))}
      </div>
    </>
  );
};

const InstructionStatusStepsSeeAllButton = ({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <button className={classes.instructionStatusStepsSeeAllButton} onClick={onClick}>
      <svg
        style={{
          transform: open ? 'rotate(180deg)' : undefined,
          transition: 'transform',
          transitionDuration: '0.3s',
        }}
        xmlns="http://www.w3.org/2000/svg"
        width="10"
        height="5"
        viewBox="0 0 10 5"
        fill="none"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.71756 0.0994334C9.54562 -0.0450118 9.27981 -0.0311149 9.12386 0.130473L5.37499 4.01501L1.62611 0.130473C1.47017 -0.0311148 1.20436 -0.0450117 1.03241 0.0994334C0.860469 0.243879 0.847498 0.491967 1.00344 0.653556L4.98836 4.78268C5.01114 4.82619 5.04304 4.8664 5.08392 4.90075C5.16675 4.97033 5.27135 5.00317 5.37497 5.00045C5.47859 5.00317 5.58322 4.97034 5.66605 4.90075C5.70694 4.8664 5.73884 4.82619 5.76161 4.78268L9.74653 0.653555C9.90248 0.491967 9.88951 0.243878 9.71756 0.0994334Z"
          fill="#1AB7CE"
        />
      </svg>

      <span>{strings.seeAll}</span>
    </button>
  );
};

export const InstructionStatusSteps: React.FC<{
  instructionStatusList: InstructionStatus[];
  isOverdue: boolean;
  type: InstructionType;
}> = ({ instructionStatusList, isOverdue, type }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const sorted = useMemo(() => {
    return orderBy(
      instructionStatusList.filter((status) => status.is_visible === true),
      ['time'],
      ['desc'],
    );
  }, [instructionStatusList]);

  const [open, setOpen] = useState(false);

  if (sorted.length === 0) return null;

  const firstStatus = sorted[0];

  return (
    <div className={clsx(classes.instructionStatusStepsWrapper)}>
      <div className={classes.instructionStatusStepsContainer}>
        <InstructionStatusStep status={firstStatus} isOverdue={isOverdue} type={type} />

        {open &&
          sorted
            .slice(1)
            .map((status) => (
              <InstructionStatusStep
                status={status}
                key={status.time}
                isOverdue={isOverdue}
                type={type}
              />
            ))}
      </div>

      <InstructionStatusStepsSeeAllButton open={open} onClick={() => setOpen(!open)} />
    </div>
  );
};
